/**
 * UserApprovalCard — one pending join request with Approve / Decline actions
 * (mobile userApprovalCard parity).
 */
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import type { PendingApprovalRequest } from '@wlb/shared/types/groupDetails';
import { Button } from '../../components/ui/Button';
import { UserIcon } from '../../components/ui/icons';
import './groups.css';

interface UserApprovalCardProps {
  request: PendingApprovalRequest;
  loading: boolean;
  disabled: boolean;
  onApprove: (groupJoinRequestId: number) => void;
  onDecline: (groupJoinRequestId: number) => void;
  onOpenProfile: (profileUserGuid?: string | null) => void;
}

export function UserApprovalCard({
  request,
  loading,
  disabled,
  onApprove,
  onDecline,
  onOpenProfile,
}: UserApprovalCardProps) {
  const { t } = useTranslation();

  const wantsToJoin = t('groupDetails.userApproval.wantsToJoin');
  const subtitle = request.invitedBy
    ? `${wantsToJoin} · ${t('groupDetails.userApproval.invitedBy', { name: request.invitedBy })}`
    : request.mutualMembers > 0
      ? `${wantsToJoin} · ${t('groupDetails.userApproval.mutualMembers', { count: request.mutualMembers })}`
      : wantsToJoin;

  return (
    <li className="group-approval__card">
      <button
        type="button"
        className="group-row__main"
        onClick={() => onOpenProfile(request.profileUserGuid)}
      >
        {request.profileImage ? (
          <img className="group-row__image" src={request.profileImage} alt="" aria-hidden />
        ) : (
          <span className="group-row__placeholder" aria-hidden><UserIcon size={24} /></span>
        )}
        <span className="group-row__meta">
          <span className="group-row__name">{request.name}</span>
          <span className="group-row__members">{subtitle}</span>
        </span>
      </button>
      <div className="group-approval__actions">
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => onDecline(request.groupJoinRequestId)}
        >
          {t('groupDetails.userApproval.decline')}
        </Button>
        <Button
          size="sm"
          disabled={disabled}
          onClick={() => onApprove(request.groupJoinRequestId)}
        >
          {t(loading ? 'common.loading' : 'groupDetails.userApproval.approve')}
        </Button>
      </div>
    </li>
  );
}
